import { Driver, IMarketplace, Method } from 'shared/types/marketplace';
import { FeeKind } from 'shared/types/marketplacePayment';
import getPixDriver from './getPixDriver';

const NOT_AVAILABLE = 'Não disponível';
const SAO_PAULO_TZ = 'America/Sao_Paulo';

export type PixStatusLabel = 'Ativo' | 'Inativo';

export interface ResolvedPixFee {
  valor: number;
  tipo: FeeKind;
}

export interface PixInfoFromMarketplace {
  firstActivation: string;
  lastPixUpdate: string;
  lastFeeChange: string;
  status: PixStatusLabel;
  currentFeeLabel: string;
  editableFee: ResolvedPixFee | null;
}

function toUtcDate(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
  const date = new Date(hasZone ? raw : `${raw}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatMarketplaceDate(
  value: string | Date | null | undefined,
): string {
  const date = toUtcDate(value);
  if (!date) return NOT_AVAILABLE;

  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: SAO_PAULO_TZ,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? '';

    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('day')}/${get('month')}/${get('year')} às ${hour}:${get('minute')}:${get('second')}`;
  } catch {
    return NOT_AVAILABLE;
  }
}

function getPixMethod(driver: Driver | null): Method | null {
  if (!driver?.methods?.length) return null;
  return driver.methods.find((method) => method.name === 'PIX') ?? null;
}

export function resolvePixFeeFromMethod(
  method: Method | null | undefined,
): ResolvedPixFee | null {
  if (!method) return null;

  let valorTaxa = 0;
  let tipoTaxa: FeeKind = 'porcentagem';

  if (method.feeType === 'VALUE' && method.transactionFeeType === 'VALUE') {
    if (method.feeValue === 0 || method.feeValue === 0.0) {
      valorTaxa = method.transactionFeeValue || 0;
    } else {
      valorTaxa = method.feeValue || 0;
    }
    tipoTaxa = 'fixo';
  } else if (method.transactionFeeValue && method.transactionFeeValue > 0) {
    valorTaxa = method.transactionFeeValue;
    tipoTaxa =
      method.transactionFeeType === 'PERCENTAGE' ? 'porcentagem' : 'fixo';
  } else if (method.feeValue && method.feeValue > 0) {
    valorTaxa = method.feeValue;
    tipoTaxa = method.feeType === 'VALUE' ? 'fixo' : 'porcentagem';
  } else if (
    method.transactionFeeValue !== undefined &&
    method.transactionFeeValue !== null
  ) {
    valorTaxa = method.transactionFeeValue || 0;
    tipoTaxa =
      method.transactionFeeType === 'PERCENTAGE' ? 'porcentagem' : 'fixo';
  } else if (method.feeValue !== undefined && method.feeValue !== null) {
    valorTaxa = method.feeValue || 0;
    tipoTaxa = method.feeType === 'VALUE' ? 'fixo' : 'porcentagem';
  } else {
    return null;
  }

  return { valor: valorTaxa, tipo: tipoTaxa };
}

export function formatResolvedPixFee(fee: ResolvedPixFee | null): string {
  if (!fee) return '';
  if (fee.tipo === 'porcentagem') {
    return `${fee.valor}%`;
  }
  return `R$ ${fee.valor}`;
}

function earliestCreatedAt(drivers: Driver[]): string | Date | null {
  const dates = drivers
    .map((driver) => driver.createdAt)
    .filter(Boolean)
    .map((value) => ({
      raw: value,
      time: toUtcDate(value)?.getTime() ?? Number.NaN,
    }))
    .filter((entry) => !Number.isNaN(entry.time))
    .sort((a, b) => a.time - b.time);

  return dates[0]?.raw ?? null;
}

export function mapPixInfoFromMarketplace(
  marketplace: Pick<IMarketplace, 'drivers'> | null | undefined,
): PixInfoFromMarketplace {
  const drivers = marketplace?.drivers ?? [];
  const pixDriver = drivers.length ? getPixDriver(drivers) : null;
  const pixMethod = getPixMethod(pixDriver);
  const itauDriver = drivers.find((driver) => driver.name === 'ITAU');
  const editableFee = resolvePixFeeFromMethod(pixMethod);
  const isActive = Boolean(pixDriver?.enabled && pixMethod?.enabled);

  return {
    firstActivation: formatMarketplaceDate(earliestCreatedAt(drivers)),
    lastPixUpdate: formatMarketplaceDate(itauDriver?.updatedAt),
    lastFeeChange: formatMarketplaceDate(pixMethod?.updatedAt),
    status: isActive ? 'Ativo' : 'Inativo',
    currentFeeLabel: isActive ? formatResolvedPixFee(editableFee) : '',
    editableFee,
  };
}
